import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Loader2,
} from "lucide-react";


import { useMembership } from "@/hooks/useMembership";
import {
  listPublishedEvents,
  listEventsForMember,
  type PublicEvent,
  type MemberEvent,
} from "@/lib/events.functions";
import { formatEventLocal, formatEventUK } from "@/lib/events";

export const Route = createFileRoute("/events_/calendar")({
  head: () => ({
    meta: [
      { title: "Events Calendar — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Monthly calendar of Recovery Trauma™ events and external trauma-related workshops, conferences and webinars.",
      },
      { property: "og:title", content: "Events Calendar — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Monthly calendar of Recovery Trauma™ events and external trauma-related workshops, conferences and webinars.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/events/calendar" },
    ],
  }),
  component: EventsCalendarPage,
});

type CalendarEvent = PublicEvent | MemberEvent;

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonthGrid(anchor: Date) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const day = first.getDay(); // Sun=0
  // Start grid on Monday
  const back = (day + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - back);
  start.setHours(0, 0, 0, 0);
  return start;
}

function EventsCalendarPage() {
  const { isAuthed } = useMembership();
  const fetchPublic = useServerFn(listPublishedEvents);
  const fetchMember = useServerFn(listEventsForMember);

  const eventsQuery = useQuery({
    queryKey: ["events-calendar", isAuthed ? "member" : "public"],
    queryFn: () => (isAuthed ? fetchMember() : fetchPublic()),
    retry: 1,
  });

  const events = (eventsQuery.data ?? []) as CalendarEvent[];

  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selected, setSelected] = useState<Date>(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const d = new Date(e.starts_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const arr = map.get(key) ?? [];
      arr.push(e);
      map.set(key, arr);
    }
    return map;
  }, [events]);

  const gridStart = startOfMonthGrid(cursor);
  const days: Date[] = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const monthLabel = cursor.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const selectedKey = `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}`;
  const dayEvents = (eventsByDay.get(selectedKey) ?? []).slice().sort(
    (a, b) => +new Date(a.starts_at) - +new Date(b.starts_at),
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:py-12">
      <header className="mb-6 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <CalendarDays className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
          Monthly view
        </div>
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">
          📅 Events Calendar
        </h1>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
          Recovery Trauma™ events and external trauma-related workshops in one
          place. Select a day to see what's on.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <LegendDot color="bg-emerald-500" label="Recovery Trauma™" />
          <LegendDot color="bg-sky-500" label="External" />
          <Link
            to="/events"
            className="ms-2 text-[var(--gold-deep)] hover:underline"
          >
            View list →
          </Link>
        </div>
      </header>

      <section className="rounded-3xl border border-gold/30 bg-card p-4 shadow-sm md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() =>
              setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
            }
            className="rounded-full border border-gold/30 p-2 hover:bg-gold/10"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="font-display text-lg text-foreground md:text-xl">
            {monthLabel}
          </div>
          <button
            type="button"
            aria-label="Next month"
            onClick={() =>
              setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
            }
            className="rounded-full border border-gold/30 p-2 hover:bg-gold/10"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1">
          {eventsQuery.isLoading ? (
            <div className="col-span-7 flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
            </div>
          ) : (
            days.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isSel = isSameDay(d, selected);
              const isToday = isSameDay(d, today);
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEv = eventsByDay.get(key) ?? [];
              const hasInternal = dayEv.some((e) => e.source !== "external");
              const hasExternal = dayEv.some((e) => e.source === "external");
              const thumb = dayEv.find((e) => e.event_image_url)?.event_image_url;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(new Date(d))}
                  className={[
                    "relative aspect-square overflow-hidden rounded-xl border p-1 text-start transition-colors",
                    isSel
                      ? "border-[var(--gold-deep)] bg-gold/15"
                      : "border-transparent hover:border-gold/30 hover:bg-gold/5",
                    inMonth ? "text-foreground" : "text-muted-foreground/50",
                  ].join(" ")}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      loading="lazy"
                      className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40"
                    />
                  ) : null}
                  <div className="relative flex items-start justify-between gap-1">
                    <span
                      className={[
                        "text-xs font-medium leading-none",
                        isToday
                          ? "flex h-5 w-5 items-center justify-center rounded-full bg-[var(--gold-deep)] text-white"
                          : thumb
                            ? "rounded bg-background/80 px-1 py-0.5"
                            : "",
                      ].join(" ")}
                    >
                      {d.getDate()}
                    </span>
                  </div>
                  <div className="relative mt-1 flex items-center gap-0.5">
                    {hasInternal ? (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                        aria-label="Recovery Trauma event"
                      />
                    ) : null}
                    {hasExternal ? (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-sky-500"
                        aria-label="External event"
                      />
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 font-display text-xl text-foreground">
          {selected.toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </h2>
        {dayEvents.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-6 text-center text-sm text-muted-foreground">
            Nothing scheduled on this day.
          </div>
        ) : (
          <div className="grid gap-4">
            {dayEvents.map((e) => (
              <DayEventCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

function DayEventCard({ event }: { event: CalendarEvent }) {
  const isExternal = event.source === "external";
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-gold/30 bg-card p-5 shadow-[var(--shadow-gold)] md:flex-row">
      {event.event_image_url ? (
        <img
          src={event.event_image_url}
          alt={event.title}
          loading="lazy"
          className="h-32 w-full flex-none rounded-xl object-cover md:h-24 md:w-32"
        />
      ) : null}
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              isExternal
                ? "bg-sky-100 text-sky-800"
                : "bg-emerald-100 text-emerald-800"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isExternal ? "bg-sky-500" : "bg-emerald-500"
              }`}
            />
            {isExternal ? "External" : "Internal"}
          </span>
          {event.host ? (
            <span className="text-xs text-muted-foreground">
              Host: {event.host}
            </span>
          ) : null}
        </div>
        <h3 className="mt-2 font-display text-lg text-foreground">
          {event.title}
        </h3>
        <div className="mt-1 space-y-0.5 text-xs">
          <div className="text-foreground">
            <span className="font-semibold text-[var(--gold-deep)]">Your time: </span>
            {formatEventLocal(event.starts_at)}
          </div>
          <div className="text-muted-foreground">
            <span className="font-semibold">UK time: </span>
            {formatEventUK(event.starts_at)}
          </div>
          <div className="inline-flex items-center gap-1 text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {event.location ?? (isExternal ? "See organiser" : "Online (Zoom)")}
          </div>
        </div>
        {event.description ? (
          <p className="mt-2 line-clamp-3 text-sm text-foreground/80">
            {event.description}
          </p>
        ) : null}
        <div className="mt-3">
          <Link
            to="/events/$slug"
            params={{ slug: event.slug }}
            className="btn-gold text-sm"
          >
            Learn More{isExternal ? <ExternalLink className="h-3.5 w-3.5" /> : null}
          </Link>
        </div>
      </div>
    </article>
  );
}
